import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { COOKIE_NAME, CONSENT_EVENT, enforceConsent } from '../lib/consent';
import { getMeasurementId, sendPageView, startGa4 } from '../lib/ga4';

// Google Analytics 4 with GDPR Consent Mode. gtag.js loads only after the visitor accepts
// (WEB-3); the measurement ID comes from the VITE_GA4_MEASUREMENT_ID build variable.

const GA_MEASUREMENT_ID = getMeasurementId();

// Last route a page view was sent for; module-level so StrictMode re-runs and repeated
// Accept events never send the same page view twice.
let lastPageViewKey = null;

// react-helmet-async writes <title> in a later animation frame (later still on lazy routes), so a
// page view sent straight from the route effect carried the previous page's title. Wait for the
// title to change; pages with the same title as the previous one send after this fallback.
// Known limit: a lazy route whose chunk takes longer than this to load still sends the previous title.
const TITLE_WAIT_MS = 1000;

export default function Analytics() {
  const location = useLocation();
  // Read the cookie during the first render so a direct load waits for its title like a navigation.
  const [hasConsent, setHasConsent] = useState(() => Cookies.get(COOKIE_NAME) === 'true');
  // Route shown while there was no consent yet: on Accept its title is already final.
  const keyBeforeConsent = useRef(null);

  // Read the consent cookie on mount and again whenever the banner reports an answer. A tab that
  // becomes visible re-checks too: another tab may have withdrawn consent meanwhile (WEB-4 Q3).
  useEffect(() => {
    const checkConsent = () => setHasConsent(Cookies.get(COOKIE_NAME) === 'true');
    const onVisible = () => {
      if (document.visibilityState === 'visible') enforceConsent();
    };
    checkConsent();
    window.addEventListener(CONSENT_EVENT, checkConsent);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(CONSENT_EVENT, checkConsent);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // After consent: load GA4 once, then send one page view per route. Each route re-checks the
  // cookie first, so a tab whose consent was withdrawn elsewhere stops instead of counting a view.
  useEffect(() => {
    if (!hasConsent || !GA_MEASUREMENT_ID || !enforceConsent()) {
      keyBeforeConsent.current = location.key;
      return;
    }
    startGa4(GA_MEASUREMENT_ID);
    if (lastPageViewKey === location.key) return;

    const key = location.key;
    const path = location.pathname + location.search;
    const send = () => {
      // Consent withdrawn while waiting (here or in another tab): send nothing.
      if (lastPageViewKey === key || !enforceConsent()) return;
      lastPageViewKey = key;
      sendPageView({ path, title: document.title, location: window.location.href });
    };

    if (keyBeforeConsent.current === key) {
      send();
      return;
    }

    // Marked as sent only when sent: a cleanup (StrictMode, a quick next navigation, unmount,
    // withdrawal) cancels the wait, and a skipped route sends no page view.
    const previousTitle = document.title;
    const observer = new MutationObserver(() => {
      if (document.title !== previousTitle) finish();
    });
    const timer = setTimeout(() => finish(), TITLE_WAIT_MS);
    const stop = () => {
      observer.disconnect();
      clearTimeout(timer);
    };
    const finish = () => {
      stop();
      send();
    };
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });
    return stop;
  }, [hasConsent, location.key, location.pathname, location.search]);

  return null;
}

/**
 * Helper function to track custom events
 * Only tracks if user has consented
 */
export const trackEvent = (eventName, eventParams = {}) => {
  const hasConsent = Cookies.get(COOKIE_NAME) === 'true';
  
  if (hasConsent && GA_MEASUREMENT_ID && typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...eventParams,
      timestamp: new Date().toISOString(),
      page: window.location.pathname
    });
    console.log('GA4 Event tracked:', eventName, eventParams);
  } else if (!hasConsent) {
    console.log('GA4 Event not tracked (no consent):', eventName);
  }
};

/**
 * Track conversion events
 */
export const trackConversion = (eventName, params = {}) => {
  trackEvent(eventName, {
    ...params,
    event_category: 'conversion'
  });
};

// ========== SPECIFIC EVENT TRACKERS ==========

/**
 * Track checklist completion
 */
export const trackChecklistComplete = (score, email, readinessLevel) => {
  trackConversion('checklist_complete', {
    checklist_score: score,
    readiness_level: readinessLevel,
    email_provided: !!email,
    value: score
  });
};

/**
 * Track Calendly booking
 */
export const trackCalendlyBooking = (eventType = 'discovery-call') => {
  trackConversion('calendly_booking', {
    event_type: eventType,
    value: 100
  });
};

/**
 * Track form submission
 */
export const trackFormSubmit = (formName, formData = {}) => {
  trackConversion('contact_form_submit', {
    form_name: formName,
    form_location: window.location.pathname,
    has_message: !!formData.message,
    message_length: formData.message?.length || 0
  });
};

/**
 * Track PDF download
 */
export const trackDownload = (fileName, fileType, additionalData = {}) => {
  trackEvent('pdf_download', {
    file_name: fileName,
    file_type: fileType,
    ...additionalData,
    event_category: 'engagement'
  });
};

/**
 * Track blog post reading
 */
export const trackBlogRead = (slug, readingTime, scrollDepth) => {
  trackEvent('blog_read', {
    blog_slug: slug,
    reading_time_seconds: readingTime,
    scroll_depth_percent: scrollDepth,
    event_category: 'engagement'
  });
};

/**
 * Track CTA clicks
 */
export const trackCTAClick = (ctaText, ctaLocation, destination) => {
  trackEvent('cta_click', {
    cta_text: ctaText,
    cta_location: ctaLocation,
    cta_destination: destination,
    event_category: 'engagement'
  });
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (depth) => {
  trackEvent('scroll_depth', {
    depth_percent: depth,
    event_category: 'engagement'
  });
};

/**
 * Track external link clicks
 */
export const trackExternalLink = (url, context) => {
  trackEvent('external_link_click', {
    link_url: url,
    link_context: context,
    event_category: 'engagement'
  });
};

/**
 * Track email signup
 */
export const trackEmailSignup = (source) => {
  trackConversion('email_signup', {
    signup_source: source,
    value: 10
  });
};

// ========== AUTOMATED TRACKERS ==========

/**
 * Initialize scroll depth tracking
 */
export const initScrollTracking = () => {
  if (typeof window === 'undefined') return;

  const tracked = new Set();
  const thresholds = [25, 50, 75, 100];

  const handleScroll = () => {
    const scrolled = window.scrollY;
    const height = document.documentElement.scrollHeight - window.innerHeight;
    const percent = Math.round((scrolled / height) * 100);

    thresholds.forEach(threshold => {
      if (percent >= threshold && !tracked.has(threshold)) {
        tracked.add(threshold);
        trackScrollDepth(threshold);
      }
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  
  return () => window.removeEventListener('scroll', handleScroll);
};

/**
 * Track time on page
 */
export const trackTimeOnPage = (pageName) => {
  const startTime = Date.now();

  return () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    trackEvent('time_on_page', {
      page_name: pageName,
      time_seconds: timeSpent,
      event_category: 'engagement'
    });
  };
};
